import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import html2pdf from 'html2pdf.js';
import { MarkdownRenderer } from '@/components/shared/MarkdownRenderer';
import { sanitizeHtml } from '@/utils/sanitize';

export interface PdfExportData {
  title: string;
  content: string;
  artifactType: string;
  artifactTypeLabel: string;
  projectName: string;
  date: string;
  isHtmlContent?: boolean;
}

export const PDF_PAGE_WIDTH_MM = 210;
export const PDF_HORIZONTAL_MARGIN_MM = 15;
export const PDF_CONTENT_WIDTH_MM = PDF_PAGE_WIDTH_MM - (PDF_HORIZONTAL_MARGIN_MM * 2);

export function formatPdfDate(isoDate: string): string {
  try {
    return new Date(isoDate).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return new Date().toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
}

function renderContentHtml(data: PdfExportData): string {
  if (data.isHtmlContent) {
    const cleaned = data.content
      .replace(/```html\n?/gi, '')
      .replace(/```json\n?/gi, '')
      .replace(/```markdown\n?/gi, '')
      .replace(/```\n?/g, '')
      .replace(/^```$/gm, '')
      .trim();
    return `<div class="persona-content">${sanitizeHtml(cleaned)}</div>`;
  }

  return renderToStaticMarkup(
    createElement(MarkdownRenderer, { content: data.content })
  );
}

export function buildPdfStyles(): string {
  return `
    .pdf-container, .pdf-container * { box-sizing: border-box; margin: 0; padding: 0; }

    .pdf-container {
      width: ${PDF_CONTENT_WIDTH_MM}mm;
      max-width: ${PDF_CONTENT_WIDTH_MM}mm;
      font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
      color: #0d0d0d;
      background: #ffffff;
      padding: 0 4mm;
      line-height: 1.5;
      overflow-wrap: break-word;
      word-break: normal;
    }
    .pdf-container * {
      max-width: 100%;
      white-space: normal;
      overflow-wrap: break-word;
      word-break: normal;
    }

    /* Header */
    .pdf-header {
      margin-bottom: 32px;
    }
    .pdf-header-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }
    .pdf-brand {
      font-family: 'Manrope', 'Segoe UI', sans-serif;
      font-weight: 700;
      font-size: 14px;
      color: #2197f1;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .pdf-date {
      font-size: 12px;
      color: #565858;
    }
    .pdf-divider {
      border: none;
      border-top: 1px solid #e0e0e0;
      margin: 12px 0;
    }
    .pdf-artifact-type {
      font-family: 'Manrope', 'Segoe UI', sans-serif;
      font-weight: 600;
      font-size: 13px;
      color: #565858;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
    }
    .pdf-title {
      font-family: 'Manrope', 'Segoe UI', sans-serif;
      font-weight: 700;
      font-size: 22px;
      line-height: 30px;
      color: #0d0d0d;
    }

    /* Content - mirrors MarkdownRenderer Tailwind classes */
    .pdf-body h1 {
      font-family: 'Manrope', 'Segoe UI', sans-serif;
      font-weight: 700;
      font-size: 22px;
      line-height: 30px;
      color: #0d0d0d;
      margin-bottom: 12px;
      margin-top: 20px;
      page-break-after: avoid;
    }
    .pdf-body h2 {
      font-family: 'Manrope', 'Segoe UI', sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      color: #0d0d0d;
      margin-bottom: 10px;
      margin-top: 18px;
      text-transform: uppercase;
      page-break-after: avoid;
    }
    .pdf-body h3 {
      font-family: 'Manrope', 'Segoe UI', sans-serif;
      font-weight: 600;
      font-size: 15px;
      line-height: 22px;
      color: #0d0d0d;
      margin-bottom: 8px;
      margin-top: 14px;
      page-break-after: avoid;
    }
    .pdf-body h4 {
      font-family: 'Manrope', 'Segoe UI', sans-serif;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      color: #0d0d0d;
      margin-bottom: 6px;
      margin-top: 10px;
      page-break-after: avoid;
    }
    .pdf-body p {
      font-size: 14px;
      line-height: 22px;
      color: #0d0d0d;
      margin-bottom: 10px;
    }
    .pdf-body ul {
      list-style-type: disc;
      padding-left: 24px;
      margin-bottom: 14px;
      font-size: 14px;
      line-height: 22px;
    }
    .pdf-body ol {
      list-style-type: decimal;
      padding-left: 24px;
      margin-bottom: 14px;
      font-size: 14px;
      line-height: 22px;
    }
    .pdf-body li {
      margin-bottom: 6px;
      page-break-inside: avoid;
      overflow-wrap: break-word;
    }
    .pdf-body li ul {
      margin-top: 4px;
      margin-bottom: 4px;
    }
    .pdf-body hr {
      border: none;
      border-top: 1px solid #e0e0e0;
      margin: 16px 0;
    }
    .pdf-body strong {
      font-weight: 600;
    }
    .pdf-body em {
      font-style: italic;
    }

    /* PositioningMap SVG container */
    .pdf-body > div:has(svg) {
      page-break-inside: avoid;
      margin: 16px 0;
      background: #f8f8f8;
      border-radius: 8px;
      padding: 12px;
      border: 1px solid #e8e8e8;
    }
    .pdf-body svg {
      width: 100%;
      height: auto;
    }
    .pdf-body table {
      width: 100%;
      max-width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      font-size: 12px;
      line-height: 18px;
      margin: 14px 0;
    }
    .pdf-body th,
    .pdf-body td {
      border: 1px solid #e0e0e0;
      padding: 6px;
      vertical-align: top;
      overflow-wrap: anywhere;
    }
    .pdf-body pre,
    .pdf-body code {
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }

    /* Persona-specific content styles */
    .persona-content h3 {
      font-family: 'Manrope', 'Segoe UI', sans-serif;
      font-weight: 600;
      font-size: 15px;
      line-height: 22px;
      color: #0d0d0d;
      margin-bottom: 12px;
      margin-top: 24px;
      page-break-after: avoid;
    }
    .persona-content h3:first-child {
      margin-top: 0;
    }
    .persona-content p {
      font-size: 14px;
      line-height: 22px;
      color: #0d0d0d;
      margin-bottom: 12px;
    }
    .persona-content ul {
      list-style-type: disc;
      padding-left: 24px;
      margin-bottom: 18px;
      font-size: 14px;
      line-height: 22px;
    }
    .persona-content li {
      margin-bottom: 6px;
      page-break-inside: avoid;
      overflow-wrap: break-word;
    }
    .persona-content blockquote {
      border-left: 3px solid #2197f1;
      padding-left: 14px;
      padding-top: 6px;
      padding-bottom: 6px;
      margin: 14px 0;
      font-style: italic;
      font-size: 14px;
      line-height: 22px;
      color: #565858;
    }
    .persona-content strong {
      font-weight: 600;
    }
  `;
}

function buildHeaderHtml(data: PdfExportData): string {
  const dateStr = formatPdfDate(data.date);

  return `
    <div class="pdf-header">
      <div class="pdf-header-top">
        <div class="pdf-brand">Креатор</div>
        <div class="pdf-date">${dateStr}</div>
      </div>
      <hr class="pdf-divider" />
      <div class="pdf-artifact-type">${data.artifactTypeLabel}</div>
      <div class="pdf-title">${data.title}</div>
      <hr class="pdf-divider" />
    </div>
  `;
}

export async function exportArtifactToPdf(data: PdfExportData): Promise<void> {
  const contentHtml = renderContentHtml(data);
  const headerHtml = buildHeaderHtml(data);
  const styles = buildPdfStyles();

  // Add styles to <head> for proper CSS application
  const styleEl = document.createElement('style');
  styleEl.id = 'pdf-export-styles';
  styleEl.textContent = styles;
  document.head.appendChild(styleEl);

  // No positioning! html2pdf.js clones this element into its own overlay.
  // position:fixed/absolute on the clone breaks html2canvas capture area.
  const container = document.createElement('div');
  container.className = 'pdf-container';
  container.style.width = `${PDF_CONTENT_WIDTH_MM}mm`;
  container.style.maxWidth = `${PDF_CONTENT_WIDTH_MM}mm`;
  container.innerHTML = `
    ${headerHtml}
    <div class="pdf-body">
      ${contentHtml}
    </div>
  `;

  document.body.appendChild(container);
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

  const filename = `${data.artifactTypeLabel} - ${data.projectName}.pdf`;

  try {
    const pdf = await html2pdf()
      .set({
        margin: [15, PDF_HORIZONTAL_MARGIN_MM, 20, PDF_HORIZONTAL_MARGIN_MM],
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: 0,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait' as const,
        },
      })
      .from(container)
      .toPdf()
      .get('pdf');

    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setTextColor(150);
      pdf.text(
        `${i} / ${totalPages}`,
        pdf.internal.pageSize.getWidth() / 2,
        pdf.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    pdf.save(filename);
  } finally {
    document.body.removeChild(container);
    document.head.removeChild(styleEl);
  }
}
